import React, { useState, useCallback, useEffect } from "react";
import AsyncSelect from 'react-select/async';
import { GetClientUserAutocomplete } from "../../Api/subscription";

const AutoCompleteSearchBox = ({ onUserSelect, selectedUser, placeholder = "Search user...", loadOnMount = true }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [hasLoadedDefaults, setHasLoadedDefaults] = useState(false);

  const fetchDefaultOptions = useCallback(async () => {
    if (hasLoadedDefaults) return;
    try {
      const res = await GetClientUserAutocomplete({ search: "" });
      const list = res?.data?.users || [];
      const options = list.map(user => ({
        value: user?._id,
        label: `${user?.name || 'Unnamed'}`
      }));
      setDefaultOptions(options);
      setHasLoadedDefaults(true);
    } catch (error) {
      console.error('Error loading initial users:', error);
    }
  }, [hasLoadedDefaults]);

  useEffect(() => {
    if (!loadOnMount) return;
    fetchDefaultOptions();
  }, [loadOnMount, fetchDefaultOptions]);

  const loadOptions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      return defaultOptions;
    }

    setIsLoading(true);
    try {
      const res = await GetClientUserAutocomplete({ search: inputValue });
      const list = res?.data?.users || [];
      return list.map(user => ({
        value: user._id,
        label: `${user?.name || 'Unnamed'}`
      }));
    } catch (error) {
      console.error('Error loading users:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [defaultOptions]);

  const handleChange = (selectedOption) => {
    onUserSelect?.(selectedOption);
  };

  return (
    <div
      className="autocomplete-search-container"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <AsyncSelect
        cacheOptions
        defaultOptions={defaultOptions}
        value={selectedUser}
        loadOptions={loadOptions}
        onChange={handleChange}
        onMenuOpen={fetchDefaultOptions}
        placeholder={placeholder}
        isLoading={isLoading}
        isClearable
        isSearchable
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2 ? "Type at least 2 characters to search" : "No users found"
        }
        styles={{
          control: (styles, state) => ({
            ...styles,
            border: state.isFocused ? "2px solid #AD5ADC" : "2px solid #00000061",
            boxShadow: state.isFocused ? "0 0 0 1px #AD5ADC" : "none",
            borderRadius: "20px",
            minHeight: 40,
            paddingLeft: 6,
            ":hover": { borderColor: state.isFocused ? "#AD5ADC" : "#cfcfcf" },
          }),
          input: (styles) => ({
            ...styles,
            color: "#000",
          }),
          placeholder: (styles) => ({
            ...styles,
            color: "#6b6b6b",
          }),
          singleValue: (styles) => ({
            ...styles,
            color: "#000",
            fontWeight: 400,
          }),
          dropdownIndicator: (styles, state) => ({
            ...styles,
            color: state.isFocused ? '#AD5ADC' : '#6b6b6b',
            ':hover': { color: '#AD5ADC' },
          }),
          menu: (styles) => ({
            ...styles,
            borderRadius: "20px",
            overflow: "hidden",
            cursor: "pointer",
            zIndex: 9999,
          }),
          option: (styles, { isFocused, isSelected }) => ({
            ...styles,
            backgroundColor: isSelected
              ? '#E8DBF3'
              : isFocused
                ? '#F5ECFA'
                : '#fff',
            color: '#000',
            cursor: 'pointer',
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        theme={(base) => ({
          ...base,
          colors: {
            ...base.colors,
            primary: '#AD5ADC',
            primary25: '#E8DBF3',
            primary50: '#D2B9EA',
          },
        })}
        menuPortalTarget={document.body}
      />
    </div>
  );
};

export default AutoCompleteSearchBox;
